/** localStorage draft for second “why previous brand” when Q2 = Yes. */
export const OTHER_PREV_BRAND_ANSWER_STORAGE_KEY = 'ldsurvey:otherPrevBrandAnswer';

export type ElaboratedReason = {
  checked: boolean;
  elaboration: string;
};

export type OtherPrevBrandAnswerAnswers = {
  taste: ElaboratedReason;
  price: ElaboratedReason;
  productFeatures: ElaboratedReason;
  brandCredibility: boolean;
  peersSmoking: boolean;
  otherReason: string;
  /** How many of 10 smokes are the first (main) product; second = 10 - this. */
  firstProductCount: number;
};

const emptyElaborated = (): ElaboratedReason => ({ checked: false, elaboration: '' });

export const emptyOtherPrevBrandAnswerAnswers = (): OtherPrevBrandAnswerAnswers => ({
  taste: emptyElaborated(),
  price: emptyElaborated(),
  productFeatures: emptyElaborated(),
  brandCredibility: false,
  peersSmoking: false,
  otherReason: '',
  firstProductCount: 5,
});

function parseFirstProductCount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 10) {
    return 5;
  }
  return value;
}

function parseElaborated(value: unknown): ElaboratedReason {
  if (typeof value !== 'object' || value === null) return emptyElaborated();
  const o = value as Record<string, unknown>;
  return {
    checked: o.checked === true,
    elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
  };
}

export function readOtherPrevBrandAnswerAnswers(): OtherPrevBrandAnswerAnswers {
  try {
    const raw = localStorage.getItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY);
    if (!raw) return emptyOtherPrevBrandAnswerAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyOtherPrevBrandAnswerAnswers();
    const o = parsed as Record<string, unknown>;
    return {
      taste: parseElaborated(o.taste),
      price: parseElaborated(o.price),
      productFeatures: parseElaborated(o.productFeatures),
      brandCredibility: o.brandCredibility === true,
      peersSmoking: o.peersSmoking === true,
      otherReason: typeof o.otherReason === 'string' ? o.otherReason : '',
      firstProductCount: parseFirstProductCount(o.firstProductCount),
    };
  } catch {
    return emptyOtherPrevBrandAnswerAnswers();
  }
}

export function writeOtherPrevBrandAnswerAnswers(data: OtherPrevBrandAnswerAnswers): void {
  try {
    localStorage.setItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearOtherPrevBrandAnswerAnswers(): void {
  try {
    localStorage.removeItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function hasAnyReasonSelected(data: OtherPrevBrandAnswerAnswers): boolean {
  return (
    data.taste.checked ||
    data.price.checked ||
    data.productFeatures.checked ||
    data.brandCredibility ||
    data.peersSmoking ||
    data.otherReason.trim().length > 0
  );
}

export function isOtherPrevBrandAnswerComplete(): boolean {
  const data = readOtherPrevBrandAnswerAnswers();
  if (!hasAnyReasonSelected(data)) return false;
  if (data.taste.checked && !data.taste.elaboration.trim()) return false;
  if (data.price.checked && !data.price.elaboration.trim()) return false;
  if (data.productFeatures.checked && !data.productFeatures.elaboration.trim()) return false;
  return Number.isInteger(data.firstProductCount) && data.firstProductCount >= 0 && data.firstProductCount <= 10;
}

export type OtherPrevBrandReasonType =
  | 'taste'
  | 'price'
  | 'productFeatures'
  | 'brandCredibility'
  | 'peersSmoking'
  | 'other';

export function getSelectedOtherPrevBrandReason(
  data: OtherPrevBrandAnswerAnswers
): { type: OtherPrevBrandReasonType; elaboration: string } | null {
  if (data.taste.checked) return { type: 'taste', elaboration: data.taste.elaboration };
  if (data.price.checked) return { type: 'price', elaboration: data.price.elaboration };
  if (data.productFeatures.checked) {
    return { type: 'productFeatures', elaboration: data.productFeatures.elaboration };
  }
  if (data.brandCredibility) return { type: 'brandCredibility', elaboration: '' };
  if (data.peersSmoking) return { type: 'peersSmoking', elaboration: '' };
  const other = data.otherReason.trim();
  if (other.length > 0) return { type: 'other', elaboration: other };
  return null;
}
